import { isFarmBasedPolicyType } from '@ourbranch/policy-types';
import { policyType as PolicyType } from '@ourbranch/lookups';
import { isCardFeeExemptTerm } from '@ourbranch/policy-utils';

/**
 * Shared credit-card-fee gating for the payment surfaces (manual charge form, pay-ahead) — #35705.
 *
 * A stored payment method is a credit card when it carries a card `brand`; bank/ACH methods carry a
 * `bankName` instead (the same brand-vs-bankName signal the dropdowns and the GraphQL `type` field
 * use). The processing fee itself is only relevant for a card payment on a farm-based policy (F/Z/Y)
 * for a brand that opts in (Forreston/CFM — `supportedFees.creditCard`), mirroring the backend gate
 * in @ourbranch/fee-utils generateFee / billing computeTransactionFee.
 *
 * These drive disclosure copy ONLY: since #35705 the fee is never ADDED on top of a manual/pay-ahead
 * charge — the member is billed exactly the amount shown (the fee is already baked into the policy's
 * schedule and, on imported terms, offset by an account credit).
 */
export const isCardPaymentMethod = (method) => !!method?.brand && !method?.bankName;

/**
 * Whether this policy/brand charges the CC processing fee at all — independent of any chosen method.
 * Use at payment-SETUP (adding/choosing a payment method, before a method exists) to decide whether to
 * tell the member a card incurs the fee and bank/ACH avoids it (#35705).
 */
// Auto carries the fee only where the brand's auto fee block opts in (CFM, #36200), mirroring the
// backend's per-LOB `fees.auto.applyCreditCardFee` gate. `policy` (optional) lets a card-fee-exempt
// term — CFM's imported term and renewals of it before the brand's cutoff (brandConfig.cardFeeExemption)
// — show no fee, matching billing, which charges none (#36230).
export const creditCardFeeAppliesToPolicy = ({ policyType, brandConfig, policy }) => {
  if (isCardFeeExemptTerm(policy, brandConfig?.cardFeeExemption)) {
    return false;
  }
  return policyType === PolicyType.Auto
    ? !!brandConfig?.fees?.auto?.applyCreditCardFee
    : isFarmBasedPolicyType(policyType) && !!brandConfig?.supportedFees?.creditCard;
};

export const creditCardFeeApplies = ({ method, policyType, brandConfig, policy }) =>
  isCardPaymentMethod(method) && creditCardFeeAppliesToPolicy({ policyType, brandConfig, policy });
